import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import "./styles/TransactionsPage.css";
import { AuthContext } from "../context/AuthContext";

const API_BASE = "/api/transactions";

function toIso(dtLocal) {
  // dtLocal: "2025-10-28T10:30"
  if (!dtLocal) return "";
  // Convert local datetime input to ISO
  const d = new Date(dtLocal);
  return d.toISOString();
}

function fromIsoToLocalInput(iso) {
  // For pre-filling datetime-local inputs if needed
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function formatDateTime(d) {
  try {
    return new Date(d).toLocaleString();
  } catch {
    return d;
  }
}

function downloadCSV(filename, rows) {
  const replacer = (key, value) => (value === null || value === undefined ? "" : value);
  const header = Object.keys(rows[0] || {});
  const csv =
    [header.join(",")]
      .concat(
        rows.map((row) =>
          header
            .map((fieldName) => {
              let v = row[fieldName];
              if (typeof v === "string") {
                // Escape quotes and wrap with quotes if commas exist
                const needsQuotes = v.includes(",") || v.includes("\n") || v.includes('"');
                v = v.replace(/"/g, '""');
                return needsQuotes ? `"${v}"` : v;
              }
              return v ?? "";
            })
            .join(",")
        )
      )
      .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export default function Transactions() {
  const { user } = useContext(AuthContext);

  const token = localStorage.getItem("token");
  const authHeader = useMemo(
    () => ({
      headers: { Authorization: `Bearer ${token}` },
    }),
    [token]
  );

  // ---------- Filters ----------
  const [filters, setFilters] = useState({
    deviceId: "",
    status: "",
    from: "",
    to: "",
    limit: 50,
  });

  // ---------- Table Data ----------
  const [loading, setLoading] = useState(false);
  const [txns, setTxns] = useState([]);
  const [error, setError] = useState("");

  // Client-side pagination (backend returns up to `limit`)
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const totalPages = Math.max(1, Math.ceil(txns.length / pageSize));
  const visibleRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return txns.slice(start, start + pageSize);
  }, [txns, page]);

  // ---------- Single Transaction View ----------
  const [lookupId, setLookupId] = useState("");
  const [oneTxn, setOneTxn] = useState(null);

  // ---------- Create Modal ----------
  const [showCreate, setShowCreate] = useState(false);
  const [createDraft, setCreateDraft] = useState({
    deviceId: "",
    upiHandle: "",
    transactionId: "",
    transactionDateTime: "", // datetime-local
    transactionAmount: "",
    transactionStatus: "SUCCESS",
    refundId: "",
  });
  const [creating, setCreating] = useState(false);

  // ---------- Refund Modal ----------
  const [showRefund, setShowRefund] = useState(false);
  const [refundTargetId, setRefundTargetId] = useState("");
  const [refundDraft, setRefundDraft] = useState({
    refundId: "",
    markStatusRefunded: true,
  });
  const [refunding, setRefunding] = useState(false);

  async function fetchList() {
    try {
      setLoading(true);
      setError("");
      const params = {};
      if (filters.deviceId) params.deviceId = filters.deviceId.trim();
      if (filters.status) params.status = filters.status;
      if (filters.from) params.from = toIso(filters.from);
      if (filters.to) params.to = toIso(filters.to);
      if (filters.limit) params.limit = Number(filters.limit) || 50;

      const { data } = await axios.get(API_BASE, {
        ...authHeader,
        params,
      });
      setTxns(data?.data || []);
      setPage(1);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || "Failed to load transactions");
    } finally {
      setLoading(false);
    }
  }

  async function fetchOne() {
    if (!lookupId.trim()) return;
    try {
      setLoading(true);
      setError("");
      const { data } = await axios.get(`${API_BASE}/${encodeURIComponent(lookupId.trim())}`, authHeader);
      setOneTxn(data?.data || null);
    } catch (e) {
      console.error(e);
      setOneTxn(null);
      setError(e?.response?.data?.message || "Transaction not found");
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e) {
    e?.preventDefault?.();
    try {
      setCreating(true);
      setError("");

      const payload = {
        deviceId: createDraft.deviceId.trim(),
        upiHandle: createDraft.upiHandle.trim(),
        transactionId: createDraft.transactionId.trim(),
        transactionDateTime: toIso(createDraft.transactionDateTime),
        transactionAmount: Number(createDraft.transactionAmount),
        transactionStatus: createDraft.transactionStatus,
        refundId: createDraft.refundId.trim() || null,
      };

      const { data } = await axios.post(API_BASE, payload, authHeader);
      // Prepend the new record for instant feedback
      setTxns((prev) => [data.data, ...prev]);
      setShowCreate(false);
      setCreateDraft({
        deviceId: "",
        upiHandle: "",
        transactionId: "",
        transactionDateTime: "",
        transactionAmount: "",
        transactionStatus: "SUCCESS",
        refundId: "",
      });
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || "Failed to create transaction");
    } finally {
      setCreating(false);
    }
  }

  function openRefundModal(tid) {
    setRefundTargetId(tid);
    setRefundDraft({ refundId: "", markStatusRefunded: true });
    setShowRefund(true);
  }

  async function handleRefund(e) {
    e?.preventDefault?.();
    if (!refundTargetId) return;
    try {
      setRefunding(true);
      setError("");
      const { data } = await axios.patch(
        `${API_BASE}/${encodeURIComponent(refundTargetId)}/refund`,
        {
          refundId: refundDraft.refundId.trim(),
          markStatusRefunded: !!refundDraft.markStatusRefunded,
        },
        authHeader
      );
      // Update in table
      setTxns((prev) =>
        prev.map((t) => (t.transactionId === refundTargetId ? data.data : t))
      );
      // If we’re viewing it in the “oneTxn” panel, refresh there too
      if (oneTxn && oneTxn.transactionId === refundTargetId) setOneTxn(data.data);
      setShowRefund(false);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || "Failed to update refund");
    } finally {
      setRefunding(false);
    }
  }

  function exportTable() {
    if (!txns.length) return;
    const rows = txns.map((t) => ({
      deviceId: t.deviceId,
      upiHandle: t.upiHandle,
      transactionId: t.transactionId,
      transactionDateTime: new Date(t.transactionDateTime).toISOString(),
      transactionAmount: t.transactionAmount,
      transactionStatus: t.transactionStatus,
      refundId: t.refundId ?? "",
      createdAt: t.createdAt,
      updatedAt: t.updatedAt,
    }));
    downloadCSV("transactions.csv", rows);
  }

  useEffect(() => {
    // Auto-load on mount
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="txn-page">
      <div className="txn-header">
        <h2>Transactions</h2>
        <div className="txn-actions">
          <button className="btn primary" onClick={() => setShowCreate(true)}>
            + New Transaction
          </button>
          <button className="btn outline" onClick={exportTable} disabled={!txns.length}>
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card filters">
        <div className="grid">
          <div className="col">
            <label>Device ID</label>
            <input
              type="text"
              value={filters.deviceId}
              onChange={(e) => setFilters({ ...filters, deviceId: e.target.value })}
              placeholder="e.g. ATM-001"
            />
          </div>
          <div className="col">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">All</option>
              <option value="SUCCESS">SUCCESS</option>
              <option value="FAILED">FAILED</option>
              <option value="PENDING">PENDING</option>
              <option value="REFUNDED">REFUNDED</option>
            </select>
          </div>
          <div className="col">
            <label>From</label>
            <input
              type="datetime-local"
              value={filters.from}
              onChange={(e) => setFilters({ ...filters, from: e.target.value })}
            />
          </div>
          <div className="col">
            <label>To</label>
            <input
              type="datetime-local"
              value={filters.to}
              onChange={(e) => setFilters({ ...filters, to: e.target.value })}
            />
          </div>
          <div className="col">
            <label>Limit</label>
            <input
              type="number"
              min={1}
              max={200}
              value={filters.limit}
              onChange={(e) => setFilters({ ...filters, limit: e.target.value })}
            />
          </div>
        </div>
        <div className="right">
          <button className="btn" onClick={fetchList} disabled={loading}>
            {loading ? "Loading..." : "Apply Filters"}
          </button>
        </div>
      </div>

      {error && <div className="alert error">{error}</div>}

      {/* Quick Lookup */}
      <div className="card lookup">
        <div className="lookup-row">
          <input
            type="text"
            placeholder="Enter transactionId to view details"
            value={lookupId}
            onChange={(e) => setLookupId(e.target.value)}
          />
          <button className="btn" onClick={fetchOne} disabled={!lookupId.trim() || loading}>
            {loading ? "..." : "Lookup"}
          </button>
        </div>
        {oneTxn && (
          <div className="lookup-result">
            <div><b>Transaction ID:</b> {oneTxn.transactionId}</div>
            <div><b>Device ID:</b> {oneTxn.deviceId}</div>
            <div><b>UPI Handle:</b> {oneTxn.upiHandle}</div>
            <div><b>Date/Time:</b> {formatDateTime(oneTxn.transactionDateTime)}</div>
            <div><b>Amount:</b> ₹{oneTxn.transactionAmount}</div>
            <div><b>Status:</b> <span className={`pill ${oneTxn.transactionStatus.toLowerCase()}`}>{oneTxn.transactionStatus}</span></div>
            <div><b>Refund ID:</b> {oneTxn.refundId || "-"}</div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="card">
        <div className="table-wrap">
          <table className="txn-table">
            <thead>
              <tr>
                <th>Date/Time</th>
                <th>Transaction ID</th>
                <th>Device</th>
                <th>UPI Handle</th>
                <th className="num">Amount</th>
                <th>Status</th>
                <th>Refund ID</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {!visibleRows.length && (
                <tr>
                  <td colSpan="8" className="empty">No transactions found</td>
                </tr>
              )}
              {visibleRows.map((t) => (
                <tr key={t.transactionId}>
                  <td title={new Date(t.transactionDateTime).toISOString()}>
                    {formatDateTime(t.transactionDateTime)}
                  </td>
                  <td className="mono">{t.transactionId}</td>
                  <td className="mono">{t.deviceId}</td>
                  <td>{t.upiHandle}</td>
                  <td className="num">₹{t.transactionAmount}</td>
                  <td>
                    <span className={`pill ${t.transactionStatus.toLowerCase()}`}>
                      {t.transactionStatus}
                    </span>
                  </td>
                  <td className="mono">{t.refundId || "-"}</td>
                  <td className="actions">
                    <button
                      className="btn small"
                      onClick={() => {
                        setLookupId(t.transactionId);
                        setOneTxn(t);
                      }}
                    >
                      View
                    </button>
                    <button
                      className="btn small outline"
                      onClick={() => openRefundModal(t.transactionId)}
                    >
                      Refund
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {txns.length > pageSize && (
          <div className="pagination">
            <button className="btn small" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
              ← Prev
            </button>
            <span>
              Page {page} / {totalPages}
            </span>
            <button
              className="btn small"
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="modal-backdrop" onClick={() => !creating && setShowCreate(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>New Transaction</h3>
            <form onSubmit={handleCreate} className="form-grid">
              <label>
                <span>Device ID</span>
                <input
                  required
                  value={createDraft.deviceId}
                  onChange={(e) => setCreateDraft({ ...createDraft, deviceId: e.target.value })}
                />
              </label>
              <label>
                <span>UPI Handle</span>
                <input
                  required
                  value={createDraft.upiHandle}
                  onChange={(e) => setCreateDraft({ ...createDraft, upiHandle: e.target.value })}
                />
              </label>
              <label>
                <span>Transaction ID</span>
                <input
                  required
                  value={createDraft.transactionId}
                  onChange={(e) => setCreateDraft({ ...createDraft, transactionId: e.target.value })}
                />
              </label>
              <label>
                <span>Date/Time</span>
                <input
                  type="datetime-local"
                  required
                  value={createDraft.transactionDateTime}
                  onChange={(e) =>
                    setCreateDraft({ ...createDraft, transactionDateTime: e.target.value })
                  }
                />
              </label>
              <label>
                <span>Amount (₹)</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  required
                  value={createDraft.transactionAmount}
                  onChange={(e) =>
                    setCreateDraft({ ...createDraft, transactionAmount: e.target.value })
                  }
                />
              </label>
              <label>
                <span>Status</span>
                <select
                  required
                  value={createDraft.transactionStatus}
                  onChange={(e) =>
                    setCreateDraft({ ...createDraft, transactionStatus: e.target.value })
                  }
                >
                  <option value="SUCCESS">SUCCESS</option>
                  <option value="FAILED">FAILED</option>
                  <option value="PENDING">PENDING</option>
                  <option value="REFUNDED">REFUNDED</option>
                </select>
              </label>
              <label>
                <span>Refund ID (optional)</span>
                <input
                  value={createDraft.refundId}
                  onChange={(e) => setCreateDraft({ ...createDraft, refundId: e.target.value })}
                />
              </label>

              <div className="modal-actions">
                <button type="button" className="btn outline" disabled={creating} onClick={() => setShowCreate(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn primary" disabled={creating}>
                  {creating ? "Saving..." : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Refund Modal */}
      {showRefund && (
        <div className="modal-backdrop" onClick={() => !refunding && setShowRefund(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Update Refund</h3>
            <form onSubmit={handleRefund} className="form-grid">
              <label>
                <span>Refund ID</span>
                <input
                  required
                  value={refundDraft.refundId}
                  onChange={(e) => setRefundDraft({ ...refundDraft, refundId: e.target.value })}
                />
              </label>
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={refundDraft.markStatusRefunded}
                  onChange={(e) =>
                    setRefundDraft({ ...refundDraft, markStatusRefunded: e.target.checked })
                  }
                />
                <span>Also set status to REFUNDED</span>
              </label>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn outline"
                  disabled={refunding}
                  onClick={() => setShowRefund(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn" disabled={refunding}>
                  {refunding ? "Updating..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
